import React, { useState, useRef } from 'react';
import { Box, Column, Optional, Row, useMediaQueries } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import themeStyles from './styles/PaymentOverview.styles';
import { useLanguage } from '../../utils/sitecoreContext';
import { getPaymentOverviewFields, PaymentMethods } from './utils';
import { usePriceAndFinanceDetails, usePaymentSummary } from '../PriceAndFinanceContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Error from './Error';

const PaymentOverviewWrapper = themeStyles.apply(Box, 'PaymentOverviewWrapper');
const TableRow = themeStyles.apply(Row, 'TableRow');
const TableColumn = themeStyles.apply(Column, 'TableColumn');
const PaymentMethodHeading = themeStyles.apply(Column, 'PaymentMethodHeading');
const ColumnsContainer = themeStyles.apply(Box, 'ColumnsContainer');
const ColumnItem = themeStyles.apply(Column, 'ColumnItem');
const CashColumn = themeStyles.apply(Column, 'CashColumn');
const CashAndFinanceContainer = themeStyles.apply(Column, 'CashAndFinanceContainer');
const MobileColumn = themeStyles.apply(Column, 'MobileColumn');

const PaymentOverview = () => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(PaymentMethods.Finance);

  const { isMobile, isSmallDesktop } = useMediaQueries();

  // Keeps fees consistently spaced on mobile
  const feesElement = useRef();
  const feesElementHeight = feesElement?.current?.clientHeight;

  const { t } = useTranslation();

  const language = useLanguage();

  const {
    isLoading: isPaymentCalculatorLoading,
    hasError: hasPaymentCalculatorError,
    data: priceAndFinanceData,
    isSellingProvince,
  } = usePriceAndFinanceDetails();
  const {
    isLoading: isPaymentSummaryLoading,
    hasError: hasPaymentSummaryError,
    data: paymentSummaryData,
  } = usePaymentSummary();

  const isMobileLayout = isMobile || isSmallDesktop;
  const showCashColumn = !isMobileLayout || selectedPaymentMethod === PaymentMethods.Cash;
  const showFinanceColumn = !isMobileLayout || selectedPaymentMethod === PaymentMethods.Finance;
  const isDataLoading = isPaymentCalculatorLoading || isPaymentSummaryLoading;
  const hasDataLoadError = hasPaymentCalculatorError || hasPaymentSummaryError;

  const paymentOverviewFields = getPaymentOverviewFields(priceAndFinanceData, paymentSummaryData, isSellingProvince);
  const hasSeparator = !isMobile;

  const feesAmountCash = isSellingProvince ? paymentOverviewFields.detailRows.fees.cashPrice : 0;
  const feesAmountFinance = isSellingProvince ? paymentOverviewFields.detailRows.fees.financePrice : 0;

  const onClickHandler = index => {
    setSelectedPaymentMethod(index);
  };

  return (
    <>
      <Optional when={!isMobileLayout}>
        <PaymentOverviewWrapper data-testid="cy-payment-overview">
          <ColumnsContainer>
            <Column>
              <PaymentMethodHeading
                isMobileLayout={isMobileLayout}
                isSelected={showCashColumn && isMobileLayout}
                onClick={() => onClickHandler(PaymentMethods.Cash)}
                data-testid="cy-cash-label"
                hasSeparator={hasSeparator}
              />
              <ColumnItem isLabel data-testid="cy-payment-label" isBold isPaymentRow isPaymentLabel>
                {paymentOverviewFields.headerRow.label}
              </ColumnItem>
              <ColumnItem isLabel data-testid="cy-payment-overview-msrp-label">
                {paymentOverviewFields.detailRows.msrp.label}
              </ColumnItem>
              <ColumnItem isLabel data-testid="cy-payment-overview-rebate-label">
                {paymentOverviewFields.detailRows.rebate.label}
              </ColumnItem>
              <ColumnItem isLabel data-testid="cy-payment-overview-fees-label">
                {paymentOverviewFields.detailRows.fees.label}
              </ColumnItem>
              <ColumnItem isLabel data-testid="cy-payment-overview-accessories-label">
                {paymentOverviewFields.detailRows.accessories.label}
              </ColumnItem>
              <ColumnItem isLabel data-testid="cy-payment-overview-warranty-label">
                {paymentOverviewFields.detailRows.warranty.label}
              </ColumnItem>
              <ColumnItem isLabel data-testid="cy-total-selling-price-label" isBold>
                {paymentOverviewFields.summaryRow.label}
                <sup data-testid="cy-total-selling-price-disclaimer-icon">*</sup>
              </ColumnItem>
            </Column>
            <CashAndFinanceContainer data-testid="cy-payment-cash-and-finance">
              <CashColumn hasError={hasDataLoadError} data-testid="cy-payment-overview-cash">
                <PaymentMethodHeading
                  isMobileLayout={isMobileLayout}
                  isSelected={showCashColumn && isMobileLayout}
                  onClick={() => onClickHandler(PaymentMethods.Cash)}
                  data-testid="cy-cash-label"
                  hasSeparator={hasSeparator}
                  hasError={hasDataLoadError}
                >
                  {t('Sub-apps.PriceAndFinance.cashLabel')}
                </PaymentMethodHeading>
                <Optional when={isDataLoading}>
                  <LoadingSpinner />
                </Optional>
                <Optional when={hasDataLoadError}>
                  <Error />
                </Optional>
                <Optional when={!isDataLoading && !hasDataLoadError}>
                  <ColumnItem data-testid="cy-cash-payment-label" isBold isPaymentRow>
                    {formatPrice(paymentOverviewFields.headerRow.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-msrp-cash">
                    {formatPrice(paymentOverviewFields.detailRows.msrp.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-rebate-cash">
                    {formatPrice(paymentOverviewFields.detailRows.rebate.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-fees-cash">
                    {formatPrice(feesAmountCash, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-accessories-cash">
                    {formatPrice(paymentOverviewFields.detailRows.accessories.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-warranty-cash">
                    {formatPrice(paymentOverviewFields.detailRows.warranty.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-total-selling-price-cash" isBold>
                    {formatPrice(paymentOverviewFields.summaryRow.cashPrice, language, 2)}
                  </ColumnItem>
                </Optional>
              </CashColumn>
              <Column data-testid="cy-payment-overview-finance">
                <PaymentMethodHeading
                  isMobileLayout={isMobileLayout}
                  isSelected={showFinanceColumn && isMobileLayout}
                  onClick={() => onClickHandler(PaymentMethods.Finance)}
                  data-testid="cy-finance-label"
                  hasError={hasDataLoadError}
                >
                  {t('Sub-apps.PriceAndFinance.financeLabel')}
                </PaymentMethodHeading>
                <Optional when={isDataLoading}>
                  <LoadingSpinner />
                </Optional>
                <Optional when={hasDataLoadError}>
                  <Error />
                </Optional>
                <Optional when={!isDataLoading && !hasDataLoadError}>
                  <ColumnItem data-testid="cy-finance-payment-label" isBold isPaymentRow>
                    {formatPrice(paymentOverviewFields.headerRow.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-msrp-finance">
                    {formatPrice(paymentOverviewFields.detailRows.msrp.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-rebate-finance">
                    {formatPrice(paymentOverviewFields.detailRows.rebate.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-fees-finance">
                    {formatPrice(feesAmountFinance, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-accessories-finance">
                    {formatPrice(paymentOverviewFields.detailRows.accessories.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-warranty-finance">
                    {formatPrice(paymentOverviewFields.detailRows.warranty.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-total-selling-price-finance" isBold>
                    {formatPrice(paymentOverviewFields.summaryRow.financePrice, language, 2)}
                  </ColumnItem>
                </Optional>
              </Column>
            </CashAndFinanceContainer>
          </ColumnsContainer>
        </PaymentOverviewWrapper>
      </Optional>

      <Optional when={isMobileLayout}>
        <PaymentOverviewWrapper data-testid="cy-payment-overview">
          <TableRow isMobile={isMobileLayout} isBold>
            <PaymentMethodHeading
              isMobileLayout={isMobileLayout}
              isSelected={showCashColumn && isMobileLayout}
              onClick={() => onClickHandler(PaymentMethods.Cash)}
              data-testid="cy-cash-label"
              hasSeparator={hasSeparator}
            >
              {t('Sub-apps.PriceAndFinance.cashLabel')}
            </PaymentMethodHeading>
            <PaymentMethodHeading
              isMobileLayout={isMobileLayout}
              isSelected={showFinanceColumn && isMobileLayout}
              onClick={() => onClickHandler(PaymentMethods.Finance)}
              data-testid="cy-finance-label"
            >
              {t('Sub-apps.PriceAndFinance.financeLabel')}
            </PaymentMethodHeading>
          </TableRow>
          <Optional when={!isMobileLayout}>
            <TableRow isBold>
              <TableColumn isLabel data-testid="cy-payment-label">
                {paymentOverviewFields.headerRow.label}
              </TableColumn>
              <Optional when={showCashColumn}>
                <TableColumn data-testid="cy-cash-payment-label">
                  <Optional when={!isDataLoading && !hasDataLoadError}>
                    {formatPrice(paymentOverviewFields.headerRow.cashPrice, language, 2)}
                  </Optional>
                </TableColumn>
              </Optional>
              <Optional when={showFinanceColumn}>
                <TableColumn data-testid="cy-finance-payment-label">
                  <Optional when={!isDataLoading && !hasDataLoadError}>
                    {formatPrice(paymentOverviewFields.headerRow.financePrice, language, 2)}
                  </Optional>
                </TableColumn>
              </Optional>
            </TableRow>
          </Optional>
          <Optional when={showCashColumn}>
            <ColumnsContainer>
              <MobileColumn>
                <ColumnItem isLabel data-testid="cy-payment-overview-msrp-label">
                  {paymentOverviewFields.detailRows.msrp.label}
                </ColumnItem>
                <ColumnItem isLabel data-testid="cy-payment-overview-rebate-label">
                  {paymentOverviewFields.detailRows.rebate.label}
                </ColumnItem>
                <ColumnItem ref={feesElement} isLabel data-testid="cy-payment-overview-fees-label">
                  {paymentOverviewFields.detailRows.fees.label}
                </ColumnItem>
                <ColumnItem isLabel data-testid="cy-payment-overview-accessories-label">
                  {paymentOverviewFields.detailRows.accessories.label}
                </ColumnItem>
                <ColumnItem isLabel data-testid="cy-payment-overview-warranty-label">
                  {paymentOverviewFields.detailRows.warranty.label}
                </ColumnItem>
                <ColumnItem isLabel data-testid="cy-total-selling-price-label" isBold>
                  {paymentOverviewFields.summaryRow.label}
                  <sup data-testid="cy-total-selling-price-disclaimer-icon">*</sup>
                </ColumnItem>
              </MobileColumn>
              <MobileColumn data-testid="cy-payment-overview-cash" hasNoResults={isDataLoading || hasDataLoadError}>
                <Optional when={isDataLoading}>
                  <LoadingSpinner />
                </Optional>
                <Optional when={hasDataLoadError}>
                  <Error />
                </Optional>
                <Optional when={!isDataLoading && !hasDataLoadError}>
                  <ColumnItem data-testid="cy-payment-overview-msrp-cash">
                    {formatPrice(paymentOverviewFields.detailRows.msrp.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-rebate-cash">
                    {formatPrice(paymentOverviewFields.detailRows.rebate.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-fees-cash" isFeesAmount height={feesElementHeight}>
                    {formatPrice(feesAmountCash, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-accessories-cash">
                    {formatPrice(paymentOverviewFields.detailRows.accessories.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-warranty-cash">
                    {formatPrice(paymentOverviewFields.detailRows.warranty.cashPrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-total-selling-price-cash" isBold>
                    {formatPrice(paymentOverviewFields.summaryRow.cashPrice, language, 2)}
                  </ColumnItem>
                </Optional>
              </MobileColumn>
            </ColumnsContainer>
          </Optional>
          <Optional when={showFinanceColumn}>
            <ColumnsContainer>
              <MobileColumn>
                <ColumnItem isLabel data-testid="cy-payment-overview-msrp-label">
                  {paymentOverviewFields.detailRows.msrp.label}
                </ColumnItem>
                <ColumnItem isLabel data-testid="cy-payment-overview-rebate-label">
                  {paymentOverviewFields.detailRows.rebate.label}
                </ColumnItem>
                <ColumnItem ref={feesElement} isLabel data-testid="cy-payment-overview-fees-label">
                  {paymentOverviewFields.detailRows.fees.label}
                </ColumnItem>
                <ColumnItem isLabel data-testid="cy-payment-overview-accessories-label">
                  {paymentOverviewFields.detailRows.accessories.label}
                </ColumnItem>
                <ColumnItem isLabel data-testid="cy-payment-overview-warranty-label">
                  {paymentOverviewFields.detailRows.warranty.label}
                </ColumnItem>
                <ColumnItem isLabel data-testid="cy-total-selling-price-label" isBold>
                  {paymentOverviewFields.summaryRow.label}
                  <sup data-testid="cy-total-selling-price-disclaimer-icon">*</sup>
                </ColumnItem>
              </MobileColumn>
              <MobileColumn data-testid="cy-payment-overview-finance" hasNoResults={isDataLoading || hasDataLoadError}>
                <Optional when={isDataLoading}>
                  <LoadingSpinner />
                </Optional>
                <Optional when={hasDataLoadError}>
                  <Error />
                </Optional>
                <Optional when={!isDataLoading && !hasDataLoadError}>
                  <ColumnItem data-testid="cy-payment-overview-msrp-finance">
                    {formatPrice(paymentOverviewFields.detailRows.msrp.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-rebate-finance">
                    {formatPrice(paymentOverviewFields.detailRows.rebate.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-fees-finance" isFeesAmount height={feesElementHeight}>
                    {formatPrice(feesAmountFinance, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-accessories-finance">
                    {formatPrice(paymentOverviewFields.detailRows.accessories.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-payment-overview-warranty-finance">
                    {formatPrice(paymentOverviewFields.detailRows.warranty.financePrice, language, 2)}
                  </ColumnItem>
                  <ColumnItem data-testid="cy-total-selling-price-finance" isBold>
                    {formatPrice(paymentOverviewFields.summaryRow.financePrice, language, 2)}
                  </ColumnItem>
                </Optional>
              </MobileColumn>
            </ColumnsContainer>
          </Optional>
        </PaymentOverviewWrapper>
      </Optional>
    </>
  );
};

export default PaymentOverview;
